import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import API_CONFIG from "../../config/api";
import "./MarketList.css";

const LOADING_MESSAGES = {
  pairs: "Loading trading pairs...",
  data: "Loading market data...",
};

const API_URL = `${API_CONFIG.BASE_URL}/v1/homepage-external-price/`;

const COIN_LOGOS = {
  BTC: '/btc-logo.svg',
  BTC1: '/btc1-logo.svg',
  ETH: '/eth-logo.svg',
  FTT: '/ftt-logo.svg',
  TRX: '/trx-logo.svg',
  USDT: '/usdt.svg',
  USDC: '/usdc.svg',
  BNB: '/bnb.svg',
  MATIC: '/matic.svg',
};

const MarketLogo = ({ symbol }) => {
  const [failed, setFailed] = useState(false);
  const source = COIN_LOGOS[symbol?.toUpperCase()];

  return source && !failed ? (
    <img
      src={source}
      alt={`${symbol} logo`}
      className="w-full h-full object-contain relative z-10"
      onError={() => setFailed(true)}
    />
  ) : (
    <span className="text-lg font-black text-white relative z-10 drop-shadow-sm">
      {symbol?.charAt(0)}
    </span>
  );
};

const MarketList = ({ onItemClick, searchQuery = "" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Increased due to compact design

  // Fetch data from API
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(API_URL);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error("Failed to fetch data:", err);
      setError(err.message || "Failed to fetch market data");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Transform API data to table format
  const transformedData = useMemo(() => {
    if (!data?.pairs_data) return [];

    return Object.entries(data.pairs_data).map(([pairCode, pairData]) => {
      const { base, quote } = pairData.pair_data;
      const price = pairData.price || 0;
      const price24h = pairData.price_24h || 0;
      const volume = pairData.volume || 0;

      // Calculate percentage change
      const priceChangePercent =
        price24h > 0 ? ((price - price24h) / price24h) * 100 : 0;

      return {
        id: pairData.pair_data.id,
        pair: pairCode,
        symbol: pairData.pair_data.code,
        base: base.code,
        quote: quote.code,
        name: pairData.pair_data.code,
        current_price: price,
        price: price,
        price_change_percentage_24h: priceChangePercent,
        change24h: priceChangePercent,
        total_volume: volume,
        volume24h: volume,
        market_cap: null, // API doesn't provide market cap
        marketCap: null,
        // Add additional data from API
        price_24h: price24h,
        price_24h_value: pairData.price_24h_value || 0,
        base_volume: pairData.base_volume,
        stack_precisions: pairData.pair_data.stack_precisions,
        is_token: base.is_token,
        blockchain_list: base.blockchain_list,
      };
    });
  }, [data]);

  // Filter data based on search query
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return transformedData;

    const query = searchQuery.toLowerCase().trim();
    return transformedData.filter((item) => {
      const symbol = (item.symbol || "").toLowerCase();
      const base = (item.base || "").toLowerCase();
      const name = (item.name || "").toLowerCase();
      const pair = (item.pair || "").toLowerCase();

      return (
        symbol.includes(query) ||
        base.includes(query) ||
        name.includes(query) ||
        pair.includes(query)
      );
    });
  }, [transformedData, searchQuery]);

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Memoized values
  const hasData = useMemo(() => {
    return filteredData && filteredData.length > 0;
  }, [filteredData]);

  const isLoading = useMemo(() => {
    return loading && !transformedData.length;
  }, [loading, transformedData]);

  const showError = useMemo(() => {
    return error && !transformedData.length;
  }, [error, transformedData]);

  const showEmpty = useMemo(() => {
    return !hasData && !isLoading && !showError;
  }, [hasData, isLoading, showError]);

  // Memoized callbacks
  const handleItemClick = useCallback(
    (item) => {
      onItemClick?.(item);
    },
    [onItemClick]
  );

  const handleRetry = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // Memoized render functions - Full-width balanced 2-column header
  const renderTableHeader = useMemo(
    () => (
      <div className="grid grid-cols-3 gap-4 px-4 sm:px-6 py-3 sm:py-4 bg-[#0E1696] rounded-full border !border-black mb-4">
        <div className="flex items-center space-x-2">
          <span className="text-base sm:text-base font-bold !text-white uppercase tracking-wider md:ml-20">
            Market
          </span>
        </div>
        <div className="flex items-center justify-center space-x-2">
          <span className="text-base sm:text-base font-bold !text-white uppercase tracking-wider">
            Price
          </span>
        </div>
        <div className="flex items-center justify-center space-x-2">
          <span className="text-base sm:text-base font-bold !text-white uppercase tracking-wider">
            Volume
          </span>
        </div>
      </div>
    ),
    []
  );


  const renderMarketItem = useCallback(
    (item, index) => {
      const {
        id,
        symbol,
        base,
        quote,
        name,
        image,
        current_price,
        price,
        price_change_percentage_24h,
        change24h,
        total_volume,
        volume24h,
        market_cap,
        marketCap,
        pair,
        volume,
      } = item;

      const displayPrice = current_price || price || 0;
      const displayChange = price_change_percentage_24h || change24h || 0;
      const displayVolume = total_volume || volume || 0;
      const displaySymbol = (symbol || base)?.toUpperCase();
      const displayName = name || base;

      return (
        <div
          className="group relative bg-[#182133]  rounded-full p-3 sm:p-4 mb-4 md:mb-6 cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-xl  hover:border-[#2A356D] "
          onClick={() => handleItemClick(item)}
          key={id || symbol || pair}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleItemClick(item);
            }
          }}
          style={{
            animationDelay: `${index * 50}ms`,
            animation: "slideInUp 0.6s ease-out forwards",
          }}
        >
          {/* Full-Width Balanced 2-Column Layout */}
          <div className="relative z-10 grid grid-cols-3 gap-4 items-center">
            {/* Market Column - Enhanced */}
            <div className="flex items-center space-x-3 sm:space-x-4">
              {/* Beautiful crypto avatar with gradient */}
              <div
                className={`w-12 h-12 rounded-full shadow-lg border-2 border-white/20 md:mr-7 flex items-center justify-center relative overflow-hidden group-hover:scale-105 transition-all duration-300 bg-[#0E1696]`}
              >
                <MarketLogo key={base} symbol={base} />
              </div>

              <div className="flex flex-col space-y-0.5">
                <div className="flex items-center space-x-2">
                  <h4 className="text-lg font-bold text-white group-hover:text-white transition-colors duration-300">
                    {displaySymbol}
                  </h4>
                </div>
                <span className="text-xs text-amber-300 group-hover:text-white transition-colors duration-300 font-medium">
                  {displayName}
                </span>
              </div>
            </div>

            {/* Price Column - Enhanced & Centered */}
            <div className="flex flex-col items-center justify-center space-y-1">
              <div className="text-xl sm:text-2xl font-bold text-white group-hover:text-white transition-colors duration-300">
                
                {displayPrice?.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 4,
                })}
                <span className="text-sm text-white font-semibold uppercase tracking-wider  px-3 py-1 rounded-full">
                  {quote}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-center space-x-2">
              <span className="text-xl sm:text-2xl font-bold text-white group-hover:text-white transition-colors duration-300">
                  {displayVolume?.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 4,
                })}
              </span>
            </div>
          </div>
          {/* Hover effect overlay */}
        </div>
      );
    },
    [handleItemClick]
  );

  const renderLoadingState = useMemo(
    () => (
      <div className="market-list-loading">
        <p>{LOADING_MESSAGES.data}</p>
      </div>
    ),
    []
  );

  const renderErrorState = useMemo(
    () => (
      <div className="market-list-error">
        <p>{error}</p>
        <button onClick={handleRetry} className="retry-button">
          Retry
        </button>
      </div>
    ),
    [error, handleRetry]
  );

  const renderEmptyState = useMemo(
    () => (
      <div className="market-list-empty">
        <p>
          {searchQuery.trim()
            ? `No markets found for "${searchQuery}"`
            : "No markets found"}
        </p>
        <small>
          {searchQuery.trim()
            ? "Try adjusting your search terms"
            : "Only showing coins available for trading on P2P1"}
        </small>
      </div>
    ),
    [searchQuery]
  );

  const renderPagination = useMemo(() => {
    if (totalPages <= 0) return null;

    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = window.innerWidth < 640 ? 2 : 3;

      if (totalPages <= maxVisiblePages) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        if (currentPage <= 3) {
          for (let i = 1; i <= Math.min(4, totalPages); i++) {
            pages.push(i);
          }
          if (totalPages > 4) {
            pages.push("...");
            pages.push(totalPages);
          }
        } else if (currentPage >= totalPages - 2) {
          pages.push(1);
          pages.push("...");
          for (let i = Math.max(totalPages - 3, 2); i <= totalPages; i++) {
            pages.push(i);
          }
        } else {
          pages.push(1);
          pages.push("...");
          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
            pages.push(i);
          }
          pages.push("...");
          pages.push(totalPages);
        }
      }

      return pages;
    };

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between mt-4 sm:mt-6 px-2 sm:px-4 py-3  rounded-full gap-3 sm:gap-0">
        <div className="text-xs text-white font-medium text-center sm:text-left">
          Showing{" "}
          <span className="text-white font-bold">{startIndex + 1}</span> to{" "}
          <span className="text-white font-bold">
            {Math.min(endIndex, filteredData.length)}
          </span>{" "}
          of{" "}
          <span className="text-white font-bold">
            {filteredData.length}
          </span>{" "}
          results
        </div>

        <div className="flex items-center space-x-1 sm:space-x-2">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="flex items-center px-2 sm:px-3 py-1.5 text-xs font-semibold text-white bg-[#2A356D] border-2 border-slate-600/60 rounded-full hover:bg-slate-600/80 hover:text-white hover:border-emerald-400/60 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300 hover:shadow-md hover:shadow-emerald-500/20"
          >
            <ChevronLeftIcon className="w-3 h-3 mr-1" />
            <span className="hidden sm:inline">Prev</span>
          </button>

          <div className="flex space-x-1 sm:space-x-2">
            {getPageNumbers().map((page, index) => (
              <button
                key={index}
                onClick={() => typeof page === "number" && setCurrentPage(page)}
                disabled={page === "..."}
                className={`px-2 sm:px-3 py-1.5 text-xs font-bold rounded-full transition-all duration-300 ${
                  page === currentPage
                    ? "bg-[#0E1696] !text-white shadow-lg  border-2 border-black "
                    : page === "..."
                    ? "text-pink-400 cursor-default px-1"
                    : "text-orange-300 bg-[#2A356D] border-2 border-slate-600/60 hover:bg-slate-600/80 hover:text-white hover:border-emerald-400/60 hover:shadow-md hover:shadow-emerald-500/20"
                }`}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            onClick={() =>
              setCurrentPage(Math.min(totalPages, currentPage + 1))
            }
            disabled={currentPage === totalPages}
            className="flex items-center px-2 sm:px-3 py-1.5 text-xs font-semibold text-white bg-[#2A356D] border-2 border-slate-600/60 rounded-full hover:bg-slate-600/80 hover:text-white hover:border-emerald-400/60 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300 hover:shadow-md hover:shadow-emerald-500/20"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRightIcon className="w-3 h-3 ml-1" />
          </button>
        </div>
      </div>
    );
  }, [currentPage, totalPages, startIndex, endIndex, filteredData.length]);

  const renderMarketItems = useMemo(
    () => (
      <div className="space-y-4 md:space-y-6">
        {paginatedData?.map((item, index) => renderMarketItem(item, index))}
      </div>
    ),
    [paginatedData, renderMarketItem]
  );

  // Early returns for different states
  if (isLoading) {
    return (
      <div className="market-list">
        {renderTableHeader}
        {renderLoadingState}
      </div>
    );
  }

  if (showError) {
    return (
      <div className="market-list">
        {renderTableHeader}
        {renderErrorState}
      </div>
    );
  }

  return (
    <div className="market-list md:px-8 sm:px-4 py-4">
      {/* Mobile: Scrollable container with both horizontal and vertical scrollbar */}
      <div className="md:hidden market-list-mobile-scroll overflow-auto max-h-[70vh]">
        <div className="min-w-[600px]">
          {renderTableHeader}
          {renderMarketItems}
        </div>
      </div>
      {/* Medium and above: Normal layout */}
      <div className="hidden md:block">
        {renderTableHeader}
        {renderMarketItems}
      </div>
      {showEmpty && renderEmptyState}
      {renderPagination}
    </div>
  );
};

export default MarketList;
